// src/components/NavComponents/SideNav.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

// أيقونات احترافية محدثة
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

import ProfileSection from './Sections/ProfileSection';
import { setTheme } from '../../store/themeSlice';
import { logout, clearPersistedState } from '../../store/Slices/userSlice';
import { persistor } from '../../store';

const SideNav = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLightTheme = useSelector((state) => state.theme.light);

  const brand = theme.palette?.brand?.main || theme.palette.primary.main;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const divider = theme.palette.divider;
  const paper = theme.palette.background.paper;

  const handleThemeToggle = () => dispatch(setTheme(!isLightTheme));

  const handleLogout = async () => {
    dispatch(logout());
    dispatch(clearPersistedState());
    await persistor.purge();
    navigate('/login');
    onClose();
  };

  // خريطة الألوان المميزة لكل عنصر
  const iconStylings = {
    Referral: { bg: alpha('#0EA5E9', 0.15), color: '#0EA5E9' }, // Sky Blue
    Security: { bg: alpha('#F43F5E', 0.15), color: '#F43F5E' }, // Rose
    'C-Box': { bg: alpha('#F59E0B', 0.15), color: '#F59E0B' }, // Amber
    Support: { bg: alpha('#22C55E', 0.15), color: '#22C55E' }, // Green
    'Help Center': { bg: alpha('#06B6D4', 0.15), color: '#06B6D4' }, // Cyan
    Blog: { bg: alpha('#8B5CF6', 0.15), color: '#8B5CF6' }, // Violet
    'About 1CryptoX': { bg: alpha('#EAB308', 0.15), color: '#EAB308' }, // Gold
  };

  const items = [
    { text: 'Referral', icon: <ShareRoundedIcon />, path: '/Referral' },
    { text: 'Security', icon: <SecurityRoundedIcon />, path: '/security' },
    { text: 'C-Box', icon: <WorkspacePremiumRoundedIcon />, path: '/c-box' },
    { text: 'Support', icon: <ChatBubbleOutlineRoundedIcon />, path: '/support/create' },
    { text: 'Help Center', icon: <HelpOutlineRoundedIcon />, path: '/help-center' },
    { text: 'Blog', icon: <ArticleRoundedIcon />, path: '/blog' },
    { text: 'About 1CryptoX', icon: <InfoRoundedIcon />, path: '/about-us' },
  ];

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 300,
          bgcolor: paper,
          color: textPrimary,
          borderRight: `1px solid ${divider}`,
          boxShadow: theme.shadows[3],
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
          Menu
        </Typography>

        <Tooltip title={isLightTheme ? 'Switch to dark mode' : 'Switch to light mode'}>
          <IconButton
            onClick={handleThemeToggle}
            size="small"
            sx={{
              borderRadius: 999,
              bgcolor: alpha(brand, 0.12),
              color: brand,
              '&:hover': { bgcolor: alpha(brand, 0.22) },
              transition: 'all .2s ease',
            }}
          >
            {isLightTheme ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: divider }} />

      <ProfileSection />

      <List sx={{ px: 1, py: 0.5 }}>
        {items.map((item) => {
          const selected = location.pathname.startsWith(item.path);
          const palette = iconStylings[item.text] || { bg: alpha(brand, 0.12), color: brand };

          return (
            <ListItemButton
              key={item.text}
              onClick={() => {
                navigate(item.path);
                onClose?.();
              }}
              selected={selected}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                px: 1.25,
                py: 1.1,
                transition: 'all .18s ease',
                '&.Mui-selected': {
                  bgcolor: alpha(brand, 0.1),
                  '&:hover': { bgcolor: alpha(brand, 0.18) },
                },
                '&:hover': { bgcolor: alpha(brand, 0.06) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: palette.bg,
                    display: 'grid',
                    placeItems: 'center',
                    color: palette.color,
                  }}
                >
                  {React.cloneElement(item.icon, { fontSize: 'small' })}
                </Box>
              </ListItemIcon>

              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: selected ? 700 : 500,
                    color: selected ? brand : textPrimary,
                  },
                }}
              />

              <ChevronRightIcon
                sx={{
                  color: selected ? brand : textSecondary,
                  transition: 'transform .18s ease, color .18s ease',
                  '.MuiListItemButton-root:hover &': {
                    transform: 'translateX(3px)',
                    color: brand,
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ mt: 'auto', borderColor: divider }} />

      <Box sx={{ p: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: brand,
            color: brand,
            '&:hover': {
              borderColor: brand,
              bgcolor: brand,
              color: theme.palette.getContrastText(brand),
            },
          }}
        >
          Log Out
        </Button>
      </Box>
    </Drawer>
  );
};

export default SideNav;
